<template>
  <div class="tasks-page mp-page">
    <!-- 操作栏：首屏只保留搜索与主操作，高级筛选可展开 -->
    <el-card class="filter-card">
      <el-row :gutter="16" align="middle">
        <el-col :span="8">
          <el-input
            v-model="searchQuery"
            placeholder="搜索任务名称"
            clearable
            @keyup.enter="handleSearch"
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
        </el-col>
        <el-col :span="16" class="action-buttons">
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            搜索
          </el-button>
          <el-button text type="primary" @click="showAdvancedFilters = !showAdvancedFilters">
            {{ showAdvancedFilters ? '收起筛选' : '更多筛选' }}
          </el-button>
          <el-button type="danger" :disabled="!selectedTasks.length" @click="batchDelete">
            <el-icon><Delete /></el-icon>
            批量删除
          </el-button>
          <el-button
            type="success"
            :disabled="!selectedTasks.length"
            :loading="exportZipLoading"
            @click="exportSelectedTasksZip"
          >
            <el-icon><Download /></el-icon>
            导出 ZIP
          </el-button>
        </el-col>
      </el-row>
      <el-row v-if="showAdvancedFilters" :gutter="16" align="middle" class="advanced-filters">
        <el-col :span="6">
          <el-select v-model="filterResult" placeholder="任务结果" clearable @change="handleSearch">
            <el-option label="进行中" value="status:running" />
            <el-option label="全部成功" value="outcome:gen_ok" />
            <el-option label="部分生成成功" value="outcome:gen_partial" />
            <el-option label="生成失败" value="outcome:gen_failed" />
            <el-option label="生成成功·过审失败" value="outcome:gen_ok_review_failed" />
            <el-option label="生成成功·部分过审" value="outcome:gen_ok_review_partial" />
            <el-option label="已取消" value="status:cancelled" />
            <el-option label="已暂停" value="status:paused" />
            <el-option label="等待中" value="status:pending" />
          </el-select>
        </el-col>
        <el-col :span="6">
          <el-select v-model="filterProvider" placeholder="生成模型" clearable @change="handleSearch">
            <el-option label="OpenRouter" value="openrouter" />
            <el-option label="DMX API" value="dmx" />
            <el-option label="火山引擎" value="volcengine" />
            <el-option label="口播转视频" value="voice2video" />
          </el-select>
        </el-col>
        <el-col :span="6">
          <el-button @click="resetFilters">
            <el-icon><Refresh /></el-icon>
            重置
          </el-button>
        </el-col>
      </el-row>
    </el-card>

    <!-- 任务列表 -->
    <el-card class="tasks-card">
      <template #header>
        <div class="card-header">
          <span>任务队列</span>
          <el-button type="primary" @click="$router.push('/create/generate')">
            <el-icon><Plus /></el-icon>
            新建任务
          </el-button>
        </div>
      </template>

      <el-table
        :data="tasks"
        v-loading="loading"
        @selection-change="handleSelectionChange"
        stripe
      >
        <el-table-column type="selection" width="55" />
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="name" label="任务名称" min-width="200">
          <template #default="{ row }">
            <div class="task-name">
              <span class="name-text">{{ displayTaskName(row) }}</span>
              <el-tag v-if="row.workflow === 'zhihu_qa'" size="small" type="warning">知乎问答</el-tag>
              <el-tag v-else-if="row.workflow === 'voice2video'" size="small" type="success">口播转视频</el-tag>
              <el-tag v-else size="small" :type="getProviderType(row.provider || row.config?.provider)">
                {{ getProviderLabel(row.provider || row.config?.provider) }}
              </el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="180">
          <template #default="{ row }">
            <div class="status-cell">
              <el-tag :type="getOutcomeType(row)">
                <el-icon v-if="row.status === 'running'" class="rotating"><Loading /></el-icon>
                {{ row.outcome_label || getStatusLabel(row.status) }}
              </el-tag>
              <el-tag
                v-if="row.status === 'running' && row.current_stage"
                size="small"
                type="info"
                class="stage-tag"
              >
                {{ getStageLabel(row.current_stage) }}
              </el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="进度" width="200">
          <template #default="{ row }">
            <div class="progress-info">
              <el-progress
                :percentage="getProgress(row)"
                :status="isGenFailedOutcome(row) ? 'exception' : ''"
                :stroke-width="8"
              />
              <span class="progress-text">
                {{ row.completed_count + row.failed_count }} / {{ row.total_count }}
              </span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="生成成功率" width="110">
          <template #default="{ row }">
            <span :class="getSuccessRateClass(row)">
              {{ getSuccessRate(row) }}%
            </span>
          </template>
        </el-table-column>
        <el-table-column label="过审" width="120">
          <template #default="{ row }">
            <span :class="getReviewClass(row)">{{ getReviewLabel(row) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="180" />
        <el-table-column label="操作" width="400" fixed="right">
          <template #default="{ row }">
            <el-button-group>
              <el-button
                v-if="row.status === 'running'"
                size="small"
                type="warning"
                @click="pauseTask(row.id)"
              >
                <el-icon><VideoPause /></el-icon>
                暂停
              </el-button>
              <el-button
                v-if="row.status === 'paused'"
                size="small"
                type="success"
                @click="resumeTask(row.id)"
              >
                <el-icon><VideoPlay /></el-icon>
                继续
              </el-button>
              <el-button
                v-if="row.status === 'running' || row.status === 'paused'"
                size="small"
                type="danger"
                @click="cancelTask(row.id)"
              >
                <el-icon><CircleClose /></el-icon>
                取消
              </el-button>
              <el-button
                size="small"
                :loading="exportZipLoading"
                @click="exportOneTaskZip(row.id)"
              >
                <el-icon><Download /></el-icon>
                导出 ZIP
              </el-button>
              <el-button
                size="small"
                @click="viewDetail(row)"
              >
                <el-icon><View /></el-icon>
                详情
              </el-button>
              <el-button
                v-if="canPublishTask(row)"
                size="small"
                type="primary"
                @click="publishTaskFromRow(row)"
              >
                <el-icon><Promotion /></el-icon>
                发布这批
              </el-button>
              <el-button
                v-if="row.status !== 'running' && row.status !== 'paused'"
                size="small"
                type="danger"
                plain
                @click="deleteTask(row.id)"
              >
                <el-icon><Delete /></el-icon>
                删除
              </el-button>
            </el-button-group>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 任务详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="任务详情"
      width="1100px"
      :close-on-click-modal="false"
      @closed="handleDetailDialogClosed"
    >
      <div v-if="currentTask" class="task-detail">
        <!-- 顶部快速操作 -->
        <div
          v-if="canPublishTask(currentTask) && publishableArticles.length"
          class="detail-quick-actions"
        >
          <el-alert
            type="success"
            :closable="false"
            show-icon
            :title="`任务已完成，共 ${publishableArticles.length} 篇文章可发布`"
          >
            <template #default>
              <div style="display: flex; align-items: center; gap: 8px; margin-top: 4px">
                <span>直接选择账号发布，或稍后在「文章管理」逐条处理</span>
                <el-button type="primary" size="small" @click="openPublishForTask">
                  <el-icon><Promotion /></el-icon>
                  发布这批（{{ publishableArticles.length }} 篇）
                </el-button>
              </div>
            </template>
          </el-alert>
        </div>

        <!-- 基本信息 -->
        <el-descriptions :column="3" border size="small">
          <el-descriptions-item label="任务ID">{{ currentTask.id }}</el-descriptions-item>
          <el-descriptions-item label="任务名称" :span="2">{{ currentTask.name }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
              <el-tag :type="getOutcomeType(currentTask)">
                <el-icon v-if="currentTask.status === 'running'" class="rotating"><Loading /></el-icon>
                {{ currentTask.outcome_label || getStatusLabel(currentTask.status) }}
              </el-tag>
              <el-tag v-if="currentTask.status === 'running' && currentTask.current_stage" size="small" type="warning">
                {{ getStageLabel(currentTask.current_stage) }}
              </el-tag>
              <el-tag size="small" :type="getReviewTagType(currentTask)" effect="plain">
                过审 {{ getReviewLabel(currentTask) }}
              </el-tag>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ currentTask.created_at }}</el-descriptions-item>
          <el-descriptions-item label="完成时间">{{ currentTask.completed_at || '—' }}</el-descriptions-item>
          <el-descriptions-item
            v-if="currentTask.workflow === 'zhihu_qa' && currentTask.config?.question"
            label="知乎问题"
            :span="3"
          >
            {{ currentTask.config.question }}
          </el-descriptions-item>
        </el-descriptions>

        <!-- 执行进度 -->
        <div class="detail-section">
          <el-progress :percentage="getProgress(currentTask)" :status="isGenFailedOutcome(currentTask) ? 'exception' : ''" :stroke-width="14" striped />
          <div class="progress-stats">
            <el-statistic title="总数" :value="currentTask.total_count" />
            <el-statistic title="生成成功" :value="currentTask.completed_count" class="success-stat" />
            <el-statistic title="生成失败" :value="currentTask.failed_count" class="error-stat" />
            <el-statistic title="过审通过" :value="currentTask.review_passed || 0" class="success-stat" />
            <el-statistic title="过审失败" :value="currentTask.review_failed || 0" class="error-stat" />
          </div>
        </div>

        <!-- 错误信息 -->
        <div v-if="currentTask.error_message" class="detail-section">
          <el-alert :title="currentTask.error_message" type="error" :closable="false" show-icon />
        </div>

        <!-- Tabs：生成配置 / 生成文章 -->
        <el-tabs v-model="detailTab" class="detail-tabs">

          <!-- Tab1：生成配置（Jobs 列表） -->
          <el-tab-pane label="📋 生成配置" name="config">
            <div v-if="currentTask.config?.jobs?.length" class="jobs-table-wrap">
              <el-table
                v-if="currentTask.config?.workflow === 'zhihu_qa'"
                :data="currentTask.config.jobs"
                size="small"
                border
                stripe
              >
                <el-table-column type="index" label="#" width="50" />
                <el-table-column prop="direction_label" label="方向名称" width="120" />
                <el-table-column label="回答核心方向" min-width="280" show-overflow-tooltip>
                  <template #default="{ row }">{{ row.direction_text || '—' }}</template>
                </el-table-column>
                <el-table-column prop="platform" label="平台" width="80" />
                <el-table-column prop="article_type" label="类型" width="80" />
              </el-table>
              <el-table v-else :data="currentTask.config.jobs" size="small" border stripe>
                <el-table-column type="index" label="#" width="50" />
                <el-table-column label="写作锚点" min-width="200" show-overflow-tooltip>
                  <template #default="{ row }">
                    {{ row.topic_name || row.topic_tag || '—' }}
                  </template>
                </el-table-column>
                <el-table-column prop="article_type" label="文章类型" width="90" />
                <el-table-column prop="platform" label="平台" width="110" />
                <el-table-column prop="prompt_type" label="提示词类型" width="100">
                  <template #default="{ row }">
                    <el-tag size="small" :type="row.prompt_type === 'reader' ? 'success' : 'primary'">
                      {{ row.prompt_type === 'reader' ? '读者向' : '作者向' }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column prop="angle_seed" label="角度" width="60" />
              </el-table>
            </div>
            <el-empty v-else description="无 Job 配置记录（旧任务不含此信息）" />
          </el-tab-pane>

          <!-- Tab2：生成文章 -->
          <el-tab-pane :label="`📄 生成文章 (${(currentTask.articles||[]).length})`" name="articles">
            <div v-if="currentTask.articles?.length">
              <el-collapse accordion>
                <el-collapse-item v-for="a in currentTask.articles" :key="a.id" :name="a.id">
                  <template #title>
                    <div class="article-collapse-title">
                      <el-tag size="small" :type="reviewTagType(a.review_status)">
                        审查{{ reviewLabel(a.review_status) }}
                      </el-tag>
                      <el-tag size="small" type="info">{{ a.platform }}</el-tag>
                      <el-tag size="small">{{ a.article_type }}</el-tag>
                      <span class="article-title-text">{{ a.title }}</span>
                      <span class="article-score">得分 {{ a.review_score }}</span>
                    </div>
                  </template>

                  <!-- 文章详情（懒加载） -->
                  <div class="article-detail-panel">
                    <el-tabs v-model="articleDetailTab[a.id]">

                      <!-- 正文 -->
                      <el-tab-pane label="📝 正文" :name="'content_' + a.id">
                        <div v-if="articleDetail[a.id]">
                          <div class="article-copy-bar">
                            <el-button size="small" @click="copyArticleBits(a.id, 'title')">复制标题</el-button>
                            <el-button size="small" @click="copyArticleBits(a.id, 'tags')">复制标签</el-button>
                            <el-button size="small" @click="copyArticleBits(a.id, 'content')">复制正文</el-button>
                          </div>
                          <div v-if="articleDetail[a.id].tags?.length" class="article-tags-row">
                            <el-tag
                              v-for="(t, ti) in articleDetail[a.id].tags"
                              :key="ti"
                              size="small"
                              type="info"
                              effect="plain"
                              style="margin: 2px"
                            >{{ t }}</el-tag>
                          </div>
                          <div class="content-preview">
                            {{ articleDetail[a.id].content }}
                          </div>
                        </div>
                        <el-button v-else size="small" @click="loadArticleDetail(a.id)">加载正文</el-button>
                      </el-tab-pane>

                      <!-- 审查详情 -->
                      <el-tab-pane label="🛡️ 审查详情" :name="'review_' + a.id">
                        <div v-if="articleDetail[a.id]">
                          <el-alert
                            v-if="sensitiveWordsOf(a.id).length"
                            type="error"
                            :closable="false"
                            show-icon
                            class="review-alert"
                            :title="`命中敏感词 ${sensitiveWordsOf(a.id).length} 个，请到「文章管理」修改或 AI 改写`"
                          >
                            <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px">
                              <el-tag
                                v-for="(w, wi) in sensitiveWordsOf(a.id)"
                                :key="wi"
                                size="small"
                                type="danger"
                                effect="light"
                              >{{ w }}</el-tag>
                            </div>
                          </el-alert>
                          <el-alert
                            v-else
                            type="success"
                            :closable="false"
                            show-icon
                            class="review-alert"
                            title="未命中敏感词，审核通过"
                          />
                          <el-descriptions
                            v-if="false"
                            :column="2"
                            border
                            size="small"
                            class="review-descriptions"
                          >
                            <el-descriptions-item
                              v-for="item in reviewFields(articleDetail[a.id])"
                              :key="item.label"
                              :label="item.label"
                            >
                              {{ item.value }}
                            </el-descriptions-item>
                          </el-descriptions>
                          <el-empty v-else description="暂无审查字段" />
                        </div>
                        <el-button v-else size="small" @click="loadArticleDetail(a.id)">加载审查信息</el-button>
                      </el-tab-pane>

                      <!-- 使用素材 -->
                      <el-tab-pane label="📦 使用素材" :name="'materials_' + a.id">
                        <div v-if="articleDetail[a.id]">
                          <div v-if="articleDetail[a.id].materials_used?.length">
                            <el-card
                              v-for="(m, mi) in articleDetail[a.id].materials_used"
                              :key="mi"
                              class="material-card"
                              shadow="never"
                            >
                              <div class="material-header">
                                <strong>素材 {{ mi + 1 }}：{{ m.title }}</strong>
                                <div>
                                  <el-tag size="small" type="success">
                                    {{ materialOriginLabel(m) }}
                                  </el-tag>
                                  <el-tag size="small" v-if="m.type">{{ m.type }}</el-tag>
                                  <el-tag size="small" type="warning" v-if="m.heat">热度 {{ m.heat }}</el-tag>
                                </div>
                              </div>
                              <div class="material-url" v-if="m.url">来源：{{ m.url }}</div>
                              <div class="material-content">{{ m.content }}</div>
                            </el-card>
                          </div>
                          <el-empty v-else description="本篇无关联素材记录" />
                        </div>
                        <el-button v-else size="small" @click="loadArticleDetail(a.id)">加载素材</el-button>
                      </el-tab-pane>

                      <!-- System Prompt -->
                      <el-tab-pane label="⚙️ System Prompt" :name="'sys_' + a.id">
                        <div v-if="articleDetail[a.id]">
                          <el-input
                            type="textarea"
                            :value="articleDetail[a.id].prompt_system"
                            readonly
                            :rows="14"
                            class="prompt-textarea"
                          />
                        </div>
                        <el-button v-else size="small" @click="loadArticleDetail(a.id)">加载提示词</el-button>
                      </el-tab-pane>

                      <!-- User Prompt -->
                      <el-tab-pane label="💬 User Prompt" :name="'user_' + a.id">
                        <div v-if="articleDetail[a.id]">
                          <el-input
                            type="textarea"
                            :value="articleDetail[a.id].prompt_user"
                            readonly
                            :rows="14"
                            class="prompt-textarea"
                          />
                        </div>
                        <el-button v-else size="small" @click="loadArticleDetail(a.id)">加载提示词</el-button>
                      </el-tab-pane>

                      <!-- LLM 原始输出 -->
                      <el-tab-pane label="🤖 LLM 原始输出" :name="'raw_' + a.id">
                        <div v-if="articleDetail[a.id]">
                          <el-input
                            type="textarea"
                            :value="articleDetail[a.id].llm_raw_output"
                            readonly
                            :rows="14"
                            class="prompt-textarea"
                          />
                        </div>
                        <el-button v-else size="small" @click="loadArticleDetail(a.id)">加载原始输出</el-button>
                      </el-tab-pane>

                    </el-tabs>
                  </div>
                </el-collapse-item>
              </el-collapse>
            </div>
            <el-empty v-else description="暂无生成文章" />
          </el-tab-pane>

          <!-- Tab3：全流程日志 -->
          <el-tab-pane :label="`🧪 全流程日志 (${(taskTraces || []).length})`" name="traces">
            <el-alert
              v-if="!traceApiAvailable"
              title="日志接口暂不可用：当前后端尚未加载 /api/tasks/{task_id}/traces。任务详情仍可查看，重启后端后即可实时看到全流程原始日志。"
              type="warning"
              :closable="false"
              show-icon
              class="trace-api-alert"
            />
            <div class="trace-toolbar">
              <el-alert
                title="实时日志：展示每个阶段的原始输入 input_data、原始输出 output_data 和 error_message"
                type="info"
                :closable="false"
                show-icon
              />
              <div class="trace-toolbar-actions">
                <el-select v-model="traceJobIndex" size="small" style="width: 220px;">
                  <el-option label="全部 Job" value="all" />
                  <el-option
                    v-for="(job, idx) in (currentTask.config?.jobs || [])"
                    :key="idx"
                    :label="`Job #${idx} - ${job.topic_name || job.topic_tag || '-'} / ${job.article_type || '-'}`"
                    :value="String(idx)"
                  />
                </el-select>
                <el-button size="small" @click="refreshTaskTraces" :loading="tracesLoading">刷新日志</el-button>
              </div>
            </div>

            <div v-loading="tracesLoading" class="trace-list">
              <el-empty v-if="!taskTraces.length" description="暂无阶段日志（任务运行后会实时出现）" />
              <el-collapse v-else>
                <el-collapse-item
                  v-for="item in taskTraces"
                  :key="item.id"
                  :name="item.id"
                >
                  <template #title>
                    <div class="trace-title-row">
                      <el-tag size="small" type="info">{{ item.stage }}</el-tag>
                      <el-tag size="small" :type="getTraceStatusType(item.status)">{{ item.status }}</el-tag>
                      <el-tag size="small" type="warning">job {{ item.job_index ?? '-' }}</el-tag>
                      <span class="trace-time">{{ item.created_at }}</span>
                    </div>
                  </template>

                  <div class="trace-content">
                    <el-alert
                      v-if="item.error_message"
                      :title="item.error_message"
                      type="error"
                      :closable="false"
                      show-icon
                      class="trace-error"
                    />

                    <div
                      v-if="item.stage === 'transcribe' && getTraceTranscript(item)"
                      class="trace-block"
                    >
                      <div class="trace-block-title">
                        ASR / 字幕转写全文
                        <el-tag size="small" type="success" style="margin-left: 8px">
                          {{ getTraceTranscriptSource(item) }}
                        </el-tag>
                        <span class="trace-meta">{{ (getTraceTranscript(item) || '').length }} 字</span>
                      </div>
                      <el-input
                        type="textarea"
                        :value="getTraceTranscript(item)"
                        readonly
                        :rows="14"
                        class="prompt-textarea transcript-textarea"
                      />
                    </div>

                    <div class="trace-block">
                      <div class="trace-block-title">输入 (input_data)</div>
                      <el-input
                        type="textarea"
                        :value="formatJson(item.input_data)"
                        readonly
                        :rows="10"
                        class="prompt-textarea"
                      />
                    </div>

                    <div class="trace-block">
                      <div class="trace-block-title">输出 (output_data)</div>
                      <el-input
                        type="textarea"
                        :value="formatJson(item.output_data)"
                        readonly
                        :rows="12"
                        class="prompt-textarea"
                      />
                    </div>
                  </div>
                </el-collapse-item>
              </el-collapse>
            </div>
          </el-tab-pane>

        </el-tabs>
      </div>
    </el-dialog>

    <!-- 发布对话框 -->
    <ArticlePublishDialog
      v-model="publishDialogVisible"
      :article-ids="publishDialogArticleIds"
      :article-platforms="publishDialogPlatforms"
      @submitted="onPublishSubmitted"
    />
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'
import ArticlePublishDialog from '../components/ArticlePublishDialog.vue'
import { usePageContext } from '../stores/pageContext'

export default {
  name: 'Tasks',
  components: { ArticlePublishDialog },
  setup() {
    const tasks = ref([])
    const loading = ref(false)
    const total = ref(0)
    const currentPage = ref(1)
    const pageSize = ref(20)
    const searchQuery = ref('')
    const showAdvancedFilters = ref(false)
    const filterResult = ref('')
    const filterProvider = ref('')
    const selectedTasks = ref([])
    const detailDialogVisible = ref(false)
    const currentTask = ref(null)
    const detailTab = ref('traces')
    const publishDialogVisible = ref(false)
    const publishDialogArticleIds = ref([])
    const publishDialogPlatforms = ref([])
    const articleDetailTab = ref({})   // { [articleId]: activeTabName }
    const articleDetail = ref({})      // { [articleId]: fullArticleData }
    const taskTraces = ref([])
    const tracesLoading = ref(false)
    const traceJobIndex = ref('all')
    const traceApiAvailable = ref(true)
    const exportZipLoading = ref(false)
    let refreshInterval = null
    let detailRefreshInterval = null

    const fetchTasks = async () => {
      loading.value = true
      try {
        const params = {
          skip: (currentPage.value - 1) * pageSize.value,
          limit: pageSize.value
        }
        if (searchQuery.value) params.name = searchQuery.value
        if (filterProvider.value) params.provider = filterProvider.value
        if (filterResult.value) {
          const [kind, val] = String(filterResult.value).split(':')
          if (kind === 'status' && val) params.status = val
          if (kind === 'outcome' && val) params.outcome = val
        }

        const res = await axios.get('/api/tasks', { params })
        tasks.value = res.data.items
        total.value = res.data.total || 0
      } catch (e) {
        ElMessage.error('获取任务列表失败')
        console.error(e)
      } finally {
        loading.value = false
      }
    }

    const handleSearch = () => {
      currentPage.value = 1
      fetchTasks()
    }

    const resetFilters = () => {
      searchQuery.value = ''
      filterResult.value = ''
      filterProvider.value = ''
      handleSearch()
    }

    const handleSizeChange = (val) => {
      pageSize.value = val
      fetchTasks()
    }

    const handleCurrentChange = (val) => {
      currentPage.value = val
      fetchTasks()
    }

    const handleSelectionChange = (selection) => {
      selectedTasks.value = selection
    }

    const downloadBlob = (blob, filename) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      window.URL.revokeObjectURL(url)
    }

    const _exportTasksZip = async (taskIds) => {
      if (!taskIds.length) {
        ElMessage.warning('没有可导出的任务')
        return
      }
      exportZipLoading.value = true
      try {
        const res = await axios.post('/api/tasks/export-zip', { task_ids: taskIds }, { responseType: 'blob' })
        const cd = res.headers['content-disposition'] || ''
        let filename = 'tasks_articles.zip'
        const m = /filename="([^"]+)"/.exec(cd)
        if (m) filename = m[1]
        downloadBlob(res.data, filename)
        ElMessage.success('已开始下载（按平台分目录）')
      } catch (e) {
        const data = e.response?.data
        if (data instanceof Blob) {
          try {
            const text = await data.text()
            const j = JSON.parse(text)
            ElMessage.error(j.detail || '导出失败')
          } catch {
            ElMessage.error('导出失败')
          }
        } else {
          ElMessage.error(e?.response?.data?.detail || e.message || '导出失败')
        }
      } finally {
        exportZipLoading.value = false
      }
    }

    const exportSelectedTasksZip = () => {
      const ids = selectedTasks.value.map(t => t.id)
      return _exportTasksZip(ids)
    }

    const exportOneTaskZip = (taskId) => _exportTasksZip([taskId])

    const batchDelete = async () => {
      try {
        await ElMessageBox.confirm(
          `确定要删除选中的 ${selectedTasks.value.length} 个任务吗？`,
          '确认删除',
          { type: 'warning' }
        )
        const ids = selectedTasks.value.map(t => t.id)
        const res = await axios.post('/api/tasks/batch-delete', { ids })
        const deleted = res.data?.deleted_count ?? ids.length
        const skipped = res.data?.skipped_running_ids || []
        ElMessage.success(`已删除 ${deleted} 个任务`)
        if (skipped.length) {
          ElMessage.warning(`跳过 ${skipped.length} 个运行中/暂停中的任务`)
        }
        selectedTasks.value = []
        fetchTasks()
      } catch (e) {
        if (e !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }

    const deleteTask = async (id) => {
      try {
        await ElMessageBox.confirm('确定要删除该任务吗？已生成文章不会被删除。', '确认删除', { type: 'warning' })
        await axios.delete(`/api/tasks/${id}`)
        ElMessage.success('任务已删除')
        fetchTasks()
      } catch (e) {
        if (e !== 'cancel') {
          ElMessage.error(e?.response?.data?.detail || '删除失败')
        }
      }
    }

    const pauseTask = async (id) => {
      try {
        await axios.post(`/api/tasks/${id}/pause`)
        ElMessage.success('任务已暂停')
        fetchTasks()
      } catch (e) {
        ElMessage.error('操作失败')
      }
    }

    const resumeTask = async (id) => {
      try {
        await axios.post(`/api/tasks/${id}/resume`)
        ElMessage.success('任务已恢复')
        fetchTasks()
      } catch (e) {
        ElMessage.error('操作失败')
      }
    }

    const cancelTask = async (id) => {
      try {
        await ElMessageBox.confirm('确定要取消该任务吗？', '确认取消', { type: 'warning' })
        await axios.post(`/api/tasks/${id}/cancel`)
        ElMessage.success('任务已取消')
        fetchTasks()
      } catch (e) {
        if (e !== 'cancel') {
          ElMessage.error('操作失败')
        }
      }
    }

    const fetchTaskDetail = async (taskId, silent = false) => {
      const res = await axios.get(`/api/tasks/${taskId}`)
      currentTask.value = res.data
      if (!silent) {
        detailTab.value = 'traces'
      }
    }

    const fetchTaskTraces = async (taskId, silent = false) => {
      tracesLoading.value = !silent
      try {
        const params = { limit: 1000 }
        if (traceJobIndex.value !== 'all') {
          params.job_index = Number(traceJobIndex.value)
        }
        const res = await axios.get(`/api/tasks/${taskId}/traces`, { params })
        traceApiAvailable.value = true
        taskTraces.value = (res.data.items || []).slice().reverse()
      } catch (e) {
        if (e?.response?.status === 404) {
          traceApiAvailable.value = false
          taskTraces.value = []
          return
        }
        throw e
      } finally {
        tracesLoading.value = false
      }
    }

    const startDetailPolling = (taskId) => {
      if (detailRefreshInterval) clearInterval(detailRefreshInterval)
      detailRefreshInterval = setInterval(async () => {
        if (!detailDialogVisible.value || !currentTask.value) return
        try {
          await fetchTaskDetail(taskId, true)
          await fetchTaskTraces(taskId, true)
        } catch (e) {
          console.error('详情实时刷新失败', e)
        }
      }, 2000)
    }

    const stopDetailPolling = () => {
      if (detailRefreshInterval) {
        clearInterval(detailRefreshInterval)
        detailRefreshInterval = null
      }
    }

    const viewDetail = async (row) => {
      try {
        traceJobIndex.value = 'all'
        traceApiAvailable.value = true
        await fetchTaskDetail(row.id)
        await fetchTaskTraces(row.id)
        articleDetail.value = {}
        articleDetailTab.value = {}
        detailDialogVisible.value = true
        startDetailPolling(row.id)
      } catch (e) {
        ElMessage.error('获取任务详情失败')
      }
    }

    const refreshTaskTraces = async () => {
      if (!currentTask.value) return
      try {
        await fetchTaskTraces(currentTask.value.id)
      } catch (e) {
        ElMessage.error('刷新日志失败')
      }
    }

    const handleDetailDialogClosed = () => {
      stopDetailPolling()
      taskTraces.value = []
      traceJobIndex.value = 'all'
      traceApiAvailable.value = true
    }

    const publishableArticles = computed(() => {
      const arts = currentTask.value?.articles || []
      // 只收 review_status 是 passed 或未设置（兼容旧任务）的文章
      return arts.filter((a) => {
        const s = a.review_status
        return !s || s === 'passed' || s === 'pending'
      })
    })

    const openPublishForTask = () => {
      const arts = publishableArticles.value
      if (!arts.length) {
        ElMessage.warning('本任务下没有可发布的文章')
        return
      }
      publishDialogArticleIds.value = arts.map((a) => a.id)
      const plats = new Set()
      arts.forEach((a) => { if (a.platform) plats.add(a.platform) })
      publishDialogPlatforms.value = Array.from(plats)
      publishDialogVisible.value = true
    }

    const onPublishSubmitted = () => {
      // 入队后简单提示，具体跳转由对话框自身处理
    }

    const publishTaskFromRow = async (row) => {
      try {
        // 复用现有 detail 拉取以获得 articles
        const res = await axios.get(`/api/tasks/${row.id}`)
        const arts = (res.data?.articles || []).filter((a) => {
          const s = a.review_status
          return !s || s === 'passed' || s === 'pending'
        })
        if (!arts.length) {
          ElMessage.warning('本任务下没有可发布的文章')
          return
        }
        publishDialogArticleIds.value = arts.map((a) => a.id)
        const plats = new Set()
        arts.forEach((a) => { if (a.platform) plats.add(a.platform) })
        publishDialogPlatforms.value = Array.from(plats)
        publishDialogVisible.value = true
      } catch (e) {
        ElMessage.error('获取任务文章失败')
      }
    }

    const loadArticleDetail = async (articleId) => {
      if (articleDetail.value[articleId]) return
      try {
        const res = await axios.get(`/api/articles/${articleId}`)
        articleDetail.value = { ...articleDetail.value, [articleId]: res.data }
        if (!articleDetailTab.value[articleId]) {
          articleDetailTab.value = { ...articleDetailTab.value, [articleId]: `content_${articleId}` }
        }
      } catch (e) {
        ElMessage.error('加载文章详情失败')
      }
    }

    const copyArticleBits = async (articleId, field) => {
      const d = articleDetail.value[articleId]
      if (!d) {
        ElMessage.warning('请先加载正文')
        return
      }
      let text = ''
      if (field === 'title') text = d.title || ''
      else if (field === 'tags') {
        const arr = Array.isArray(d.tags) ? d.tags : []
        text = arr.map((x) => String(x).trim()).filter(Boolean).join(' ')
      } else if (field === 'content') text = d.content || ''
      try {
        await navigator.clipboard.writeText(text)
        ElMessage.success(field === 'title' ? '已复制标题' : field === 'tags' ? '已复制标签' : '已复制正文')
      } catch (e) {
        ElMessage.error('复制失败')
      }
    }

    const getStatusType = (status) => {
      const map = {
        'completed': 'success',
        'partial': 'warning',
        'running': 'primary',
        'failed': 'danger',
        'paused': 'warning',
        'pending': 'info',
        'cancelled': 'info'
      }
      return map[status] || 'info'
    }

    const getStatusLabel = (status) => {
      const map = {
        'completed': '已完成',
        'partial': '部分生成成功',
        'running': '进行中',
        'failed': '生成失败',
        'paused': '已暂停',
        'pending': '等待中',
        'cancelled': '已取消'
      }
      return map[status] || status
    }

    const getOutcomeType = (row) => {
      const o = row?.outcome || row?.status
      const map = {
        running: 'primary',
        paused: 'warning',
        pending: 'info',
        cancelled: 'info',
        cancelled_partial: 'info',
        gen_failed: 'danger',
        gen_partial: 'warning',
        gen_ok: 'success',
        gen_ok_review_failed: 'warning',
        gen_ok_review_partial: 'warning',
        completed: 'success',
        failed: 'danger',
        partial: 'warning',
      }
      return map[o] || getStatusType(row?.status)
    }

    const isGenFailedOutcome = (row) => {
      const o = row?.outcome || row?.status
      return o === 'gen_failed' || o === 'failed'
    }

    const canPublishTask = (row) => {
      if (!row) return false
      const terminal = ['completed', 'partial', 'cancelled'].includes(row.status)
      if (!terminal) return false
      if ((row.review_passed || 0) > 0) return true
      return (row.completed_count || 0) > 0 && (row.outcome === 'gen_ok' || row.outcome === 'gen_partial' || row.status === 'completed' || row.status === 'partial')
    }

    const getReviewLabel = (row) => {
      const total = row?.review_total || 0
      if (!total) return '—'
      const passed = row.review_passed || 0
      const failed = row.review_failed || 0
      if (failed === total) return `过审失败 ${passed}/${total}`
      if (passed === total) return `已过审 ${passed}/${total}`
      if (failed > 0 && passed > 0) return `部分过审 ${passed}/${total}`
      return `${passed}/${total}`
    }

    const getReviewClass = (row) => {
      const total = row?.review_total || 0
      if (!total) return 'success-rate'
      const passed = row.review_passed || 0
      const failed = row.review_failed || 0
      if (failed === total) return 'success-rate low'
      if (passed === total) return 'success-rate high'
      return 'success-rate medium'
    }

    const getReviewTagType = (row) => {
      const total = row?.review_total || 0
      if (!total) return 'info'
      const passed = row.review_passed || 0
      const failed = row.review_failed || 0
      if (failed === total) return 'danger'
      if (passed === total) return 'success'
      return 'warning'
    }

    const getProviderType = (provider) => {
      const map = {
        'openrouter': 'primary',
        'dmx': 'success',
        'dmxapi': 'success',
        'volcengine': 'warning',
        'voice2video': 'success'
      }
      return map[provider] || 'info'
    }

    const getProviderLabel = (provider) => {
      const map = {
        'openrouter': 'OpenRouter',
        'dmx': 'DMX API',
        'dmxapi': 'DMX API',
        'volcengine': '火山引擎',
        'voice2video': '口播转视频'
      }
      return map[provider] || provider
    }

    const displayTaskName = (row) => {
      return row?.title || row?.name || '未命名任务'
    }

    const getProgress = (row) => {
      if (!row || row.total_count === 0) return 0
      return Math.round((row.completed_count + row.failed_count) / row.total_count * 100)
    }

    const getSuccessRate = (row) => {
      const completed = row.completed_count || 0
      const total = row.completed_count + row.failed_count
      if (total === 0) return 0
      return Math.round(completed / total * 100)
    }

    const getStageLabel = (stage) => {
      if (!stage) return ''
      // 搜索阶段：searching:2/5 表示第 2 组 / 共 5 组（搜索慢时进度条仍为 0% 属正常）
      if (stage.startsWith('searching:')) {
        const rest = stage.slice('searching:'.length)
        return `🔍 搜索素材中 (${rest})`
      }
      // 生成阶段：generating:3/10 表示已完成 3 篇 / 共 10 篇
      if (stage.startsWith('generating:')) {
        const rest = stage.slice('generating:'.length)
        return `✍️ 生成文章中 (${rest})`
      }
      if (stage.startsWith('zhihu_generating:')) {
        const rest = stage.slice('zhihu_generating:'.length)
        return `💬 知乎问答生成中 (${rest})`
      }
      const map = {
        searching: '🔍 搜索素材中',
        generating: '✍️ 生成文章中',
        zhihu_generating: '💬 知乎问答生成中',
        voice2video_script: '📝 口播文案生成中',
        voice2video_materials: '🎞️ 视频素材规划中',
        voice2video_judge: '✅ 视频质检中',
        dedup_check: '🔄 去重检查中',
        reviewing: '🛡️ 内容审查中',
        saving: '💾 保存文章中'
      }
      return map[stage] || stage
    }

    const getSuccessRateClass = (row) => {
      const rate = getSuccessRate(row)
      if (rate >= 90) return 'success-rate high'
      if (rate >= 70) return 'success-rate medium'
      return 'success-rate low'
    }

    const getTraceStatusType = (status) => {
      const map = {
        completed: 'success',
        running: 'primary',
        failed: 'danger'
      }
      return map[status] || 'info'
    }

    const formatJson = (data) => {
      try {
        return JSON.stringify(data ?? {}, null, 2)
      } catch (e) {
        return String(data ?? '')
      }
    }

    const getTraceTranscript = (item) => {
      const out = item?.output_data
      if (!out || typeof out !== 'object') return ''
      return (out.text || out.transcript || '').trim()
    }

    const getTraceTranscriptSource = (item) => {
      const out = item?.output_data
      if (!out || typeof out !== 'object') return '转写'
      return out.source || out.transcript_source || '转写'
    }

    const materialOriginLabel = (material) => {
      if (material?.source_origin_label) return material.source_origin_label
      const origin = material?.source_origin || ''
      const map = {
        current_search: '当前搜索',
        current_search_after_link_dedup: '当前搜索（去重后）',
        current_search_raw_fallback: '当前搜索（兜底回退）',
        pool_preassigned_unused: '素材池预分配',
        pool_same_topic_unused: '素材池同主题未使用',
        pool_same_type_unused: '素材池同类型未使用'
      }
      return map[origin] || (origin ? `来源: ${origin}` : '来源未知')
    }

    const reviewLabel = (s) => ({ passed: '通过', failed: '未通过', pending: '待审查' }[s] || '待审查')
    const reviewTagType = (s) => ({ passed: 'success', failed: 'danger', pending: 'info' }[s] || 'info')

    // 取某文章详情里的敏感词命中（优先实时检测 sensitive，回退 review_details）
    const sensitiveWordsOf = (articleId) => {
      const a = articleDetail.value?.[articleId]
      if (!a) return []
      if (a.sensitive && Array.isArray(a.sensitive.hit_words)) return a.sensitive.hit_words
      const sd = a.review_details?.sensitive || a.review_details?.review?.sensitive
      if (sd && Array.isArray(sd.hit_words)) return sd.hit_words
      if (sd && Array.isArray(sd.hits)) return [...new Set(sd.hits.map((h) => h.word))]
      return []
    }

    const reviewDetails = (article) => {
      const raw = article?.review_details || {}
      return raw?.review || raw
    }

    const semanticReview = (article) => {
      return reviewDetails(article)?.semantic_review || {}
    }

    const reviewReason = (article) => {
      if (!article || article.review_status === 'passed') return ''
      const sem = semanticReview(article)
      const reasons = []
      if (sem.truncated) reasons.push(sem.truncation_detail || '正文疑似被截断')
      if (sem.pass_review === false && sem.summary) reasons.push(sem.summary)
      if (sem.huaben_intro === 'none') reasons.push('未识别到话本小说相关介绍')
      const details = reviewDetails(article)
      const platformSuggestions = details?.platform_fit?.suggestions || []
      if (platformSuggestions.length) reasons.push(platformSuggestions[0])
      if (sem.ok === false) reasons.push(`语义审查失败: ${sem.error || '未知错误'}`)
      if (reasons.length === 0) reasons.push('未返回具体审查原因，请查看 review_details 原始字段')
      return reasons.filter(Boolean).join('；')
    }

    const reviewFields = (article) => {
      if (!article) return []
      const sem = semanticReview(article)
      const details = reviewDetails(article)
      const politicalHits = details?.political?.hits || []
      const pornHits = details?.porn?.hits || []
      const violenceHits = details?.violence?.hits || []
      const platformSuggestions = details?.platform_fit?.suggestions || []
      const huabenDeprecated = details?.huaben_safety?.deprecated_features || []
      const huabenNegative = details?.huaben_safety?.negative_patterns || []
      const rows = []
      const reasonText = reviewReason(article)
      if (typeof article.review_score === 'number') rows.push({ label: '审查得分', value: article.review_score })
      rows.push({ label: '涉政命中', value: String(politicalHits.length) })
      rows.push({ label: '涉黄命中', value: String(pornHits.length) })
      rows.push({ label: '黑恶命中', value: String(violenceHits.length) })
      if (article.review_status !== 'passed' && reasonText) rows.push({ label: '审查原因', value: reasonText })
      if (platformSuggestions.length) rows.push({ label: '平台建议', value: platformSuggestions.join('；') })
      if (huabenDeprecated.length) rows.push({ label: '话本下线功能命中', value: huabenDeprecated.join('、') })
      if (huabenNegative.length) rows.push({ label: '话本负面命中', value: huabenNegative.join('、') })
      if (typeof sem.ok !== 'undefined') rows.push({ label: '语义审查执行', value: sem.ok ? '是' : '否' })
      if (typeof sem.truncated !== 'undefined') rows.push({ label: '是否截断', value: sem.truncated ? '是' : '否' })
      if (sem.truncation_detail) rows.push({ label: '截断依据', value: sem.truncation_detail })
      if (sem.huaben_intro) rows.push({ label: '话本触达', value: sem.huaben_intro })
      if (sem.huaben_detail) rows.push({ label: '话本判定说明', value: sem.huaben_detail })
      if (typeof sem.pass_review !== 'undefined') rows.push({ label: '语义判定通过', value: sem.pass_review ? '是' : '否' })
      if (sem.summary) rows.push({ label: '语义结论', value: sem.summary })
      if (typeof sem.ok === 'undefined') rows.push({ label: '语义审查字段', value: '缺失（旧数据或未执行）' })
      return rows
    }

    watch(traceJobIndex, async () => {
      if (!currentTask.value || !detailDialogVisible.value) return
      await refreshTaskTraces()
    })

    onMounted(() => {
      fetchTasks()
      // 自动刷新进行中任务
      refreshInterval = setInterval(() => {
        const hasRunning = tasks.value.some(t => t.status === 'running')
        if (hasRunning) fetchTasks()
      }, 5000)
    })

    onUnmounted(() => {
      if (refreshInterval) clearInterval(refreshInterval)
      stopDetailPolling()
    })

    // 供侧栏 Agent 读取：任务列表 + 打开的任务详情及其文章
    usePageContext(() => {
      const t = detailDialogVisible.value ? currentTask.value : null
      const arts = t?.articles || []
      return {
        kind: t ? 'task_detail' : 'task_list',
        description: t
          ? '生成任务详情；selected_ids 为该任务下可发布（过审）的文章 ID'
          : '生成任务列表；selected_task_ids 为用户勾选的任务',
        selected_ids: t ? publishableArticles.value.map((a) => a.id) : [],
        selected_task_ids: selectedTasks.value.map((x) => x.id),
        task: t
          ? {
              id: t.id,
              name: t.name,
              status: t.status,
              outcome: t.outcome_label,
              total: t.total_count,
              completed: t.completed_count,
              failed: t.failed_count,
            }
          : undefined,
        items: t
          ? arts.map((a) => ({ id: a.id, title: a.title, platform: a.platform, review_status: a.review_status }))
          : tasks.value.map((x) => ({ id: x.id, name: x.name, status: x.status })),
      }
    })

    return {
      tasks,
      loading,
      total,
      currentPage,
      pageSize,
      searchQuery,
      showAdvancedFilters,
      filterResult,
      filterProvider,
      selectedTasks,
      detailDialogVisible,
      currentTask,
      detailTab,
      publishDialogVisible,
      publishDialogArticleIds,
      publishDialogPlatforms,
      publishableArticles,
      openPublishForTask,
      onPublishSubmitted,
      publishTaskFromRow,
      articleDetailTab,
      articleDetail,
      taskTraces,
      tracesLoading,
      traceJobIndex,
      traceApiAvailable,
      exportZipLoading,
      exportSelectedTasksZip,
      exportOneTaskZip,
      loadArticleDetail,
      copyArticleBits,
      refreshTaskTraces,
      handleDetailDialogClosed,
      handleSearch,
      resetFilters,
      handleSizeChange,
      handleCurrentChange,
      handleSelectionChange,
      batchDelete,
      deleteTask,
      pauseTask,
      resumeTask,
      cancelTask,
      viewDetail,
      displayTaskName,
      getStatusType,
      getStatusLabel,
      getOutcomeType,
      isGenFailedOutcome,
      canPublishTask,
      getReviewLabel,
      getReviewClass,
      getReviewTagType,
      getProviderType,
      getProviderLabel,
      getProgress,
      getSuccessRate,
      getSuccessRateClass,
      getStageLabel,
      getTraceStatusType,
      formatJson,
      getTraceTranscript,
      getTraceTranscriptSource,
      materialOriginLabel,
      reviewReason,
      reviewFields,
      reviewLabel,
      reviewTagType,
      sensitiveWordsOf
    }
  }
}
</script>

<style scoped>
.tasks-page {
  padding: 0;
}

.advanced-filters {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--mp-border, #e8ecf3);
}
.filter-card {
  margin-bottom: 20px;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.task-name {
  display: flex;
  align-items: center;
  gap: 8px;
}

.name-text {
  font-weight: 500;
}

.progress-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.progress-text {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
}

.success-rate {
  font-weight: bold;
}

.success-rate.high {
  color: #67C23A;
}

.success-rate.medium {
  color: #E6A23C;
}

.success-rate.low {
  color: #F56C6C;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.task-detail {
  .detail-quick-actions {
    margin-bottom: 12px;
  }

  .detail-section {
    margin-top: 16px;
  }

  .progress-stats {
    display: flex;
    justify-content: space-around;
    margin-top: 16px;

    :deep(.el-statistic__content) {
      font-size: 22px;
      font-weight: bold;
    }
  }

  .success-stat :deep(.el-statistic__content) { color: #67C23A; }
  .error-stat :deep(.el-statistic__content) { color: #F56C6C; }

  .detail-tabs {
    margin-top: 20px;
  }

  .trace-toolbar {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 12px;
  }

  .trace-api-alert {
    margin-bottom: 10px;
  }

  .trace-toolbar-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
  }

  .trace-list {
    max-height: 560px;
    overflow-y: auto;
    padding-right: 2px;
  }

  .trace-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
  }

  .trace-time {
    margin-left: auto;
    color: #909399;
    font-size: 12px;
    padding-right: 10px;
  }

  .trace-content {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 4px;
  }

  .trace-error {
    margin-bottom: 4px;
  }

  .trace-block-title {
    font-size: 12px;
    color: #606266;
    margin: 2px 0 6px;
    font-weight: 600;
  }

  .trace-meta {
    margin-left: 8px;
    color: #909399;
    font-weight: 400;
  }

  .transcript-textarea :deep(textarea) {
    font-size: 13px;
    line-height: 1.6;
    color: #303133;
  }

  .jobs-table-wrap {
    max-height: 360px;
    overflow-y: auto;
  }

  .article-collapse-title {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  .article-title-text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
  }

  .article-score {
    font-size: 12px;
    color: #909399;
    white-space: nowrap;
    margin-left: auto;
    padding-right: 12px;
  }

  .article-detail-panel {
    padding: 8px 0;
  }

  .article-copy-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
  }

  .article-tags-row {
    margin-bottom: 10px;
  }

  .content-preview {
    white-space: pre-wrap;
    line-height: 1.8;
    font-size: 14px;
    color: #303133;
    max-height: 400px;
    overflow-y: auto;
    padding: 8px;
    background: #fafafa;
    border-radius: 4px;
  }

  .material-card {
    margin-bottom: 12px;
    border: 1px solid #e4e7ed;

    .material-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 6px;
      gap: 8px;
    }

    .material-url {
      font-size: 12px;
      color: #909399;
      margin-bottom: 6px;
      word-break: break-all;
    }

    .material-content {
      white-space: pre-wrap;
      font-size: 13px;
      line-height: 1.7;
      color: #606266;
      max-height: 200px;
      overflow-y: auto;
    }
  }

  .prompt-textarea {
    :deep(textarea) {
      font-family: 'Courier New', Courier, monospace;
      font-size: 12px;
      line-height: 1.6;
      background: #1e1e1e;
      color: #d4d4d4;
      border-radius: 4px;
    }
  }

  .review-alert {
    margin-bottom: 10px;
  }

  .review-descriptions {
    margin-bottom: 6px;
  }
}

.status-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
}

.stage-tag {
  font-size: 11px;
}

.rotating {
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
